import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, Observable, of, OperatorFunction } from 'rxjs';
import { PairingStatus } from '../enums/PairingStatus';
import { Employee } from '../models/generated/employee';
import { PersonNamePipe } from '../pipes/PersonName';
import { CustomHttpError } from '../utils/CustomHttpError';
import { EmployeeService } from './employee.service';
import { EnvironmentService } from './environment.service';
import { ToastService } from './toast.service';

export enum LogAction {
  CREATE = 'CREATE',
  STATUS = 'STATUS',
  PREPARED = 'PREPARED',
  ANNOTATION = 'ANNOTATION',
  MEDICATION = 'MEDICATION',
  START_TIME = 'START_TIME',
  DRIP_CHAMBER_DETECTED = 'DRIP_CHAMBER_DETECTED',
  PAIRING_STATE = 'PAIRING_STATE',
  DOSE_HIS = 'DOSE_HIS',
  DOSE_EDITED = 'DOSE_EDITED',
  FLOW_RATE_HIS = 'FLOW_RATE_HIS',
  FLOW_RATE_EDITED = 'FLOW_RATE_EDITED',
  CARRIER_SOLUTION_HIS = 'CARRIER_SOLUTION_HIS',
  CARRIER_SOLUTION_EDITED = 'CARRIER_SOLUTION_EDITED',
  TOTAL_VOLUME_EDITED = 'TOTAL_VOLUME_EDITED'
}

export interface LogExtended extends Log {
  message: string;
  icon: string;
  performedByName?: string;
}

export interface Log {
  version: string;
  timestamp: string;
  performedById: string;
  action: LogAction;
  previousValue: string;
  newValue: string;
  details: string;
}

export interface AcknowledgeItem {
  therapyJobId: string;
  prescriptionCurrentVersion: string;
  prescriptionVersionConfirmed: string;
  ackTimestamp: string;
  therapyJobDeleted: boolean;
}

const URLS = {
  THERAPY_JOB_PRESCRIPTION_DIFF: (therapyJobId: string) => `/therapyjob/${therapyJobId}/prescription/diff`,
  THERAPY_JOB_DIFF: (therapyJobId: string) => `/therapyjob/${therapyJobId}/diff`,
  THERAPY_JOB_DEVICE_DIFF: (therapyJobId: string) => `/therapyjob/${therapyJobId}/device/diff`
};

@Injectable({
  providedIn: 'root'
})
export class ActivityLogService {
  private readonly client: HttpClient = inject(HttpClient);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly env: EnvironmentService = inject(EnvironmentService);
  private readonly employeeService: EmployeeService = inject(EmployeeService);

  private readonly personNamePipe = new PersonNamePipe();

  private readonly extendLogsName: OperatorFunction<[Array<Log>, Array<Employee>], Array<LogExtended>> = map(
    ([logs, employees]: [Array<Log>, Array<Employee>]) => {
      return logs
        .map((log) => {
          const performerName = this.resolvePerformerName(log, employees);

          return {
            ...log,
            message: this.getMessage(log, performerName),
            icon: this.getIcon(log),
            performedByName: performerName
          };
        })
        .toSorted((a, b) => {
          if (a.action === LogAction.CREATE && b.action !== LogAction.CREATE) {
            return 1;
          }
          if (b.action === LogAction.CREATE && a.action !== LogAction.CREATE) {
            return -1;
          }
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });
    }
  );

  public getLogs(therapyJobId: string, prescriptionId: string): Observable<Array<LogExtended>> {
    if (!therapyJobId) {
      return of([]);
    }

    const sources = [
      this.getResourceDiff(URLS.THERAPY_JOB_PRESCRIPTION_DIFF(therapyJobId)),
      this.getResourceDiff(URLS.THERAPY_JOB_DIFF(therapyJobId)),
      this.getResourceDiff(URLS.THERAPY_JOB_DEVICE_DIFF(therapyJobId)),
      this.employeeService.getEmployees()
    ];

    if (prescriptionId) {
      return forkJoin(sources).pipe(
        map(
          ([prescriptionLogs, medAdminLogs, deviceLogs, employees]) =>
            [[...medAdminLogs, ...prescriptionLogs, ...deviceLogs], employees] as [Array<Log>, Array<Employee>]
        ),
        this.extendLogsName
      );
    } else {
      return forkJoin(sources.slice(1)).pipe(
        map(
          ([medAdminLogs, deviceLogs, employees]) =>
            [[...medAdminLogs, ...deviceLogs], employees] as [Array<Log>, Array<Employee>]
        ),
        this.extendLogsName
      );
    }
  }

  private getResourceDiff(url: string, fromVersion: number = 1, toVersion: number = null): Observable<Array<Log>> {
    let params = new HttpParams().set('from', fromVersion);
    if (toVersion) {
      params = params.set('to', toVersion);
    }
    return this.client
      .get<Array<Log>>(`${this.env.getBaseRestUrl()}${url}`, {
        params
      })
      .pipe(
        catchError((err: CustomHttpError) => {
          console.warn(`Error requesting ${url}`, err);

          this.toastService.genericError(err.traceId);
          return of([]);
        })
      );
  }

  public getPrescriptionLogs(
    therapyJobId: string,
    fromVersion: number = 1,
    toVersion: number = null
  ): Observable<Array<LogExtended>> {
    return forkJoin([
      this.getResourceDiff(URLS.THERAPY_JOB_PRESCRIPTION_DIFF(therapyJobId), fromVersion, toVersion),
      this.employeeService.getEmployees()
    ]).pipe(this.extendLogsName);
  }

  public getPendingAcknowledgement(jobIds: Array<string>): Observable<Array<AcknowledgeItem>> {
    if (!jobIds.length) {
      return of([]);
    }
    return this.client
      .post<Array<AcknowledgeItem>>(`${this.env.getBaseRestUrl()}/therapyjob/ack?status=PENDING`, jobIds)
      .pipe(
        catchError((err: CustomHttpError) => {
          console.warn('Error requesting pending acknowledgements', err);

          this.toastService.genericError(err.traceId);
          return of([]);
        })
      );
  }

  public setAcknowledged(jobId: string, version: string): Observable<void> {
    let params = new HttpParams().set('version', version);
    return this.client
      .put<void>(`${this.env.getBaseRestUrl()}/therapyjob/${jobId}/ack`, null, {
        params
      })
      .pipe(
        catchError((err: CustomHttpError) => {
          console.warn('Error setting acknowledge', err);

          this.toastService.genericError(err.traceId);
          return of();
        })
      );
  }

  private getMessage(log: Log, performedByName?: string): string {
    const performer = performedByName || this.translateService.instant('UnknownUser');

    switch (log.action) {
      case LogAction.CREATE:
        return this.translateService.instant('ActivityLogCreateChangeMessage', { performer });

      case LogAction.STATUS:
        return this.translateService.instant('ActivityLogStatusChangeMessage', { performer });

      case LogAction.MEDICATION:
        return this.translateService.instant('ActivityLogMedicationChangeMessage', { performer });

      case LogAction.ANNOTATION:
        if (log.previousValue === '' || !log.previousValue || log.previousValue === '-') {
          return this.translateService.instant('ActivityLogAnnotationCreatedMessage', { performer });
        }
        return this.translateService.instant('ActivityLogAnnotationUpdatedMessage', { performer });

      case LogAction.PREPARED:
        // From null to a value
        if (!log.previousValue && log.newValue) {
          return this.translateService.instant('ActivityLogAnnotationPreparedMessage', { performer });
        }

        // From a date1 to date2
        if (!!log.previousValue && !!log.newValue && log.previousValue !== log.newValue) {
          return this.translateService.instant('ActivityLogAnnotationPreparationTimeChanged', { performer });
        }

        // From a value to null
        return this.translateService.instant('ActivityLogAnnotationUnPreparedMessage', { performer });

      case LogAction.DRIP_CHAMBER_DETECTED:
        return this.translateService.instant(this.getDripChamberTranslationKey(log.newValue), {
          performer,
          deviceId: `<b>${log.details}</b>`
        });

      case LogAction.PAIRING_STATE:
        return this.translateService.instant(this.getPairingStateTranslationKey(log.newValue), {
          performer,
          deviceId: `<b>${log.details}</b>`
        });

      case LogAction.FLOW_RATE_HIS:
        return this.translateService.instant('ActivityLogFlowRatePrescribedChangeMessage', { performer });
      case LogAction.FLOW_RATE_EDITED:
        return this.translateService.instant('ActivityLogFlowRateEditedChangeMessage', { performer });

      case LogAction.DOSE_HIS:
        return this.translateService.instant('ActivityLogDosePrescribedChangeMessage', { performer });
      case LogAction.DOSE_EDITED:
        return this.translateService.instant('ActivityLogDoseEditedChangeMessage', { performer });

      case LogAction.CARRIER_SOLUTION_HIS:
      case LogAction.CARRIER_SOLUTION_EDITED:
        return this.translateService.instant('ActivityLogCarrierChangeMessage', { performer });

      case LogAction.TOTAL_VOLUME_EDITED:
        return this.translateService.instant('ActivityLogTotalVolumeEditedChangeMessage', { performer });

      case LogAction.START_TIME:
        return this.translateService.instant('ActivityLogStartTimeChangeMessage');

      default:
        return this.translateService.instant('ActivityLogDefaultChangeMessage', {
          action: log.action,
          performer
        });
    }
  }

  private getDripChamberTranslationKey(newValue: string): string {
    return newValue === 'true' ? 'ActivityLogDripChamberDetectedMessage' : 'ActivityLogDripChamberDetachedMessage';
  }

  private getPairingStateTranslationKey(newValue: string): string {
    switch (newValue) {
      case PairingStatus.PAIRED:
        return 'ActivityLogPairedMessage';
      case PairingStatus.IN_PAIRING:
        return 'ActivityLogInPairedMessage';
      case PairingStatus.UN_PAIRED:
        return 'ActivityLogUnpairedMessage';
      default:
        return '';
    }
  }

  private getPairingStateIcon(newValue: string): string {
    switch (newValue) {
      case PairingStatus.PAIRED:
        return 'link';
      case PairingStatus.IN_PAIRING:
        return 'link';
      case PairingStatus.UN_PAIRED:
        return 'unlink';
      default:
        return '';
    }
  }

  private getIcon(log: Log): string {
    switch (log.action) {
      case LogAction.CREATE:
        return 'create';

      case LogAction.STATUS:
      case LogAction.PREPARED:
        return 'edit-write';

      case LogAction.ANNOTATION:
        return 'message-reminder';

      case LogAction.MEDICATION:
      case LogAction.FLOW_RATE_HIS:
      case LogAction.DOSE_HIS:
      case LogAction.CARRIER_SOLUTION_HIS:
      case LogAction.FLOW_RATE_EDITED:
      case LogAction.DOSE_EDITED:
      case LogAction.CARRIER_SOLUTION_EDITED:
        return 'warning';

      case LogAction.PAIRING_STATE:
        return this.getPairingStateIcon(log.newValue);

      case LogAction.DRIP_CHAMBER_DETECTED:
        return log.newValue === 'true' ? 'check' : 'warning';

      default:
        return 'edit-write';
    }
  }

  private resolvePerformerName(log: Log, employees: Array<Employee>): string {
    if (log.performedById === 'HIS') {
      return 'HIS';
    }

    const performer: Employee | undefined = employees.find((e) => e.id === log.performedById);
    return performer ? this.personNamePipe.transform(performer) : this.translateService.instant('UnknownUser');
  }
}
